import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const EarningsBreakdown = ({ data, pendingEarnings }) => {
  const [timeFilter, setTimeFilter] = useState('6months');
  const [selectedTrailer, setSelectedTrailer] = useState('all');

  const maxRevenue = Math.max(...(data?.monthlyRevenue?.map(d => d?.revenue) || [0]));

  const calculateTotalRevenue = () => {
    return data?.monthlyRevenue?.reduce((sum, month) => sum + month?.revenue, 0) || 0;
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div>
              <label className="font-caption text-xs text-muted-foreground mb-1 block">Time Period</label>
              <select
                value={timeFilter}
                onChange={(e) => setTimeFilter(e?.target?.value)}
                className="px-4 py-2 border border-border rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="3months">Last 3 Months</option>
                <option value="6months">Last 6 Months</option>
                <option value="year">Last Year</option>
                <option value="all">All Time</option>
              </select>
            </div>
            <div>
              <label className="font-caption text-xs text-muted-foreground mb-1 block">Trailer</label>
              <select
                value={selectedTrailer}
                onChange={(e) => setSelectedTrailer(e?.target?.value)}
                className="px-4 py-2 border border-border rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Trailers</option>
                <option value="1">Heavy Duty Utility Trailer</option>
                <option value="2">Enclosed Cargo Trailer</option>
                <option value="3">Flatbed Equipment Trailer</option>
              </select>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Info" size={16} className="text-muted-foreground" strokeWidth={2} />
            <p className="font-caption text-xs text-muted-foreground">Showing data for selected period</p>
          </div>
        </div>
      </div>

      {/* Revenue Chart */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-2xl font-semibold text-foreground flex items-center space-x-2">
            <Icon name="BarChart3" size={24} strokeWidth={2} className="text-primary" />
            <span>Revenue Breakdown</span>
          </h2>
          <div className="text-right">
            <p className="font-caption text-xs text-muted-foreground">Total Revenue</p>
            <p className="font-heading text-2xl font-bold text-primary">${calculateTotalRevenue()?.toLocaleString()}</p>
          </div>
        </div>

        <div className="flex items-end justify-between space-x-2 md:space-x-4 h-64 mb-4">
          {data?.monthlyRevenue?.map((month, index) => (
            <div key={index} className="flex-1 flex flex-col items-center group">
              <div className="w-full flex items-end justify-center h-52 mb-2">
                <div
                  className="w-full bg-gradient-to-t from-primary to-primary/60 rounded-t-md transition-all duration-500 hover:opacity-80 relative cursor-pointer"
                  style={{ height: `${(month?.revenue / maxRevenue) * 100}%` }}
                >
                  <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-foreground text-background px-3 py-1.5 rounded text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-elevation-3">
                    <div className="font-bold">${month?.revenue?.toLocaleString()}</div>
                    <div className="text-[10px] opacity-75">{month?.month} 2026</div>
                  </div>
                </div>
              </div>
              <p className="font-caption text-xs md:text-sm text-muted-foreground">{month?.month}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-border">
          <div className="text-center">
            <p className="font-caption text-xs text-muted-foreground mb-1">Avg Monthly</p>
            <p className="font-body text-lg font-semibold text-foreground">
              ${Math.round(calculateTotalRevenue() / data?.monthlyRevenue?.length)?.toLocaleString()}
            </p>
          </div>
          <div className="text-center">
            <p className="font-caption text-xs text-muted-foreground mb-1">Peak Month</p>
            <p className="font-body text-lg font-semibold text-foreground">Oct</p>
          </div>
          <div className="text-center">
            <p className="font-caption text-xs text-muted-foreground mb-1">Growth Rate</p>
            <p className="font-body text-lg font-semibold text-success flex items-center justify-center space-x-1">
              <Icon name="TrendingUp" size={14} strokeWidth={2} />
              <span>+18%</span>
            </p>
          </div>
          <div className="text-center">
            <p className="font-caption text-xs text-muted-foreground mb-1">Projection</p>
            <p className="font-body text-lg font-semibold text-foreground">$3.8K</p>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-card rounded-lg shadow-elevation-2 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-body font-semibold text-foreground">Booking Frequency</h3>
            <Icon name="Calendar" size={20} className="text-accent" strokeWidth={2} />
          </div>
          <p className="font-heading text-4xl font-bold text-foreground mb-2">{data?.bookingFrequency}</p>
          <p className="font-caption text-sm text-muted-foreground mb-4">Total bookings</p>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-body text-muted-foreground">This month</span>
              <span className="font-body font-medium text-foreground">8 bookings</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="font-body text-muted-foreground">Avg per month</span>
              <span className="font-body font-medium text-foreground">7.8 bookings</span>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-elevation-2 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-body font-semibold text-foreground">Average Rental Rate</h3>
            <Icon name="DollarSign" size={20} className="text-success" strokeWidth={2} />
          </div>
          <p className="font-heading text-4xl font-bold text-foreground mb-2">${data?.averageRentalRate}</p>
          <p className="font-caption text-sm text-muted-foreground mb-4">Per day</p>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-body text-muted-foreground">Highest rate</span>
              <span className="font-body font-medium text-foreground">$110/day</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="font-body text-muted-foreground">Lowest rate</span>
              <span className="font-body font-medium text-foreground">$75/day</span>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-elevation-2 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-body font-semibold text-foreground">Seasonal Trends</h3>
            <Icon name="Sun" size={20} className="text-warning" strokeWidth={2} />
          </div>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="font-body text-sm text-muted-foreground">Peak Season</span>
                <span className="font-body font-medium text-success">{data?.seasonalTrends?.peak}</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-success h-2 rounded-full" style={{ width: '85%' }}></div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="font-body text-sm text-muted-foreground">Low Season</span>
                <span className="font-body font-medium text-warning">{data?.seasonalTrends?.low}</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-warning h-2 rounded-full" style={{ width: '45%' }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Earnings Alert */}
      {pendingEarnings?.amount > 0 && (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-6">
          <div className="flex items-start space-x-4">
            <div className="w-10 h-10 bg-warning rounded-full flex items-center justify-center flex-shrink-0">
              <Icon name="Clock" size={20} color="white" strokeWidth={2} />
            </div>
            <div className="flex-1">
              <h3 className="font-body font-semibold text-foreground mb-2">Pending Earnings</h3>
              <p className="font-body text-sm text-muted-foreground mb-3">
                You have <span className="font-bold text-foreground">${pendingEarnings?.amount?.toLocaleString()}</span> from{' '}
                <span className="font-bold text-foreground">{pendingEarnings?.bookingsCount} bookings</span> that will be available for withdrawal on{' '}
                <span className="font-bold text-foreground">
                  {new Date(pendingEarnings?.estimatedPayoutDate)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </span>.
              </p>
              <div className="flex items-center space-x-2">
                <Icon name="Info" size={14} className="text-muted-foreground" strokeWidth={2} />
                <p className="font-caption text-xs text-muted-foreground">
                  Minimum withdrawal threshold: ${pendingEarnings?.minimumThreshold}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EarningsBreakdown;